'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { isValidElement, Fragment, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as CustomRenderer from 'UIComponents/utils/propTypes/customRenderer';
import * as ScreenSizes from '../../constants/layout/ScreenSizes';
import { SCREEN_SIZE_ORDER } from '../../constants/layout';
import { MediaQueryStylesContext } from './MediaQueryStylesContext';
import { getMediaQueryClass, mediaQueryClassRegex } from './MediaQueryStyles';

const getRendererComputedClasses = (Renderer, ...args) => {
  if ( /*#__PURE__*/isValidElement(Renderer)) {
    args.unshift(Renderer.props.className);
  }

  return classNames(...args);
};

const renderChild = (child, props) => {
  return CustomRenderer.render(child, props, {
    mergeType: 'aggressive'
  });
};

const MediaQuery = ({
  alternate = null,
  children = null,
  minScreenSize,
  maxScreenSize,
  className = ''
}) => {
  const {
    hasInjectedGlobalStyles
  } = useContext(MediaQueryStylesContext);
  const getPassedMediaQueryClasses = useCallback(() => {
    return classNames(...(className.match(mediaQueryClassRegex) || ""));
  }, [className]);
  const renderChildren = useCallback(() => {
    if (!children) {
      return null;
    }

    const mediaQueryClassConfig = {
      [getMediaQueryClass(ScreenSizes.XS, minScreenSize)]: !!minScreenSize,
      [getMediaQueryClass(maxScreenSize, 'min')]: !!maxScreenSize
    };
    const computedClasses = getRendererComputedClasses(children, getPassedMediaQueryClasses(), mediaQueryClassConfig);
    return renderChild(children, {
      className: computedClasses
    });
  }, [children, maxScreenSize, minScreenSize, getPassedMediaQueryClasses]);
  const renderAlternate = useCallback(() => {
    if (!alternate) {
      return null;
    }

    const mediaQueryClassConfig = {
      [getMediaQueryClass(minScreenSize, maxScreenSize)]: minScreenSize && maxScreenSize,
      [getMediaQueryClass(minScreenSize, 'min')]: minScreenSize && !maxScreenSize,
      [getMediaQueryClass(maxScreenSize, 'max')]: maxScreenSize && !minScreenSize
    };
    const computedClasses = getRendererComputedClasses(alternate, getPassedMediaQueryClasses(), mediaQueryClassConfig);
    return renderChild(alternate, {
      className: computedClasses
    });
  }, [alternate, maxScreenSize, minScreenSize, getPassedMediaQueryClasses]);
  useEffect(() => {
    if (!hasInjectedGlobalStyles) {
      console.warn('[MediaQuery] Global styles not injected.');
    }
  }, [hasInjectedGlobalStyles]);
  useEffect(() => {
    if (!(minScreenSize || maxScreenSize)) {
      console.warn('[MediaQuery] Either min/maxScreenSize required.');
    }
  }, [maxScreenSize, minScreenSize]);

  if (!hasInjectedGlobalStyles) {
    return children;
  }

  if (!(minScreenSize || maxScreenSize)) {
    return children;
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [renderChildren(), renderAlternate()]
  });
};

MediaQuery.propTypes = {
  alternate: CustomRenderer.propType,
  children: CustomRenderer.propType,
  className: PropTypes.string,
  minScreenSize: PropTypes.oneOf(SCREEN_SIZE_ORDER),
  maxScreenSize: PropTypes.oneOf(SCREEN_SIZE_ORDER)
};
export default MediaQuery;